import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  FileText,
  LayoutDashboard,
  Menu,
  MessageSquare,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LeftSidebar from '@/js/components/sidebars/LeftSidebar';
import Breadcrumb from '@/js/components/Breadcrumb';
import ProjectDescriptionScreen from '@/js/screens/Buyer/Project/ProjectDescriptionScreen';
import ShortlistedManufacturerScreen from '@/js/screens/Buyer/Project/ShortlistedManufacturerScreen';
import { useDispatch, useSelector } from 'react-redux';
import createApiClient from '@/api/axiosInstance';
import MessagesScreen from '@/js/screens/Buyer/Project/MessagesScreen';
import DocumentsScreen from '@/js/screens/Buyer/Project/DocumentsScreen';
import AllPartsScreen from '@/js/screens/Buyer/Project/AllPartsScreen';
import { clearProjectDetailsAction } from '@/js/reduxstore/actions/projectActions';

const steps = [
  { id: 1, label: 'Search Manufacturer' },
  { id: 2, label: 'Project Dashboard' },
];

const screenEnum = {
  PROJECT_DETAILS: 'PROJECT_DETAILS',
  ALL_PARTS: 'ALL_PARTS',
  MANUFACTURERS: 'MANUFACTURERS',
  DOCUMENTS: 'DOCUMENTS',
  MESSAGES: 'MESSAGES',
};

const ProjectDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const apiClient = createApiClient(navigate, dispatch);

  const switchTo = location.state?.switchTo;

  const [sectionId, setSectionId] = useState(2);
  const [screen, setScreen] = useState(switchTo || screenEnum.PROJECT_DETAILS);

  const { projectId, projectName } = useParams();

  const menuItems = [
    { label: 'Project Dashboard', value: screenEnum.PROJECT_DETAILS },
    {
      label: 'Manufacturers',
      value: screenEnum.MANUFACTURERS,
    },
    ...(projectId !== undefined
      ? [{ label: 'Documents', value: screenEnum.DOCUMENTS }]
      : []),
    ...(projectId !== undefined
      ? [{ label: 'Messages', value: screenEnum.MESSAGES }]
      : []),
  ];

  const screenFunction = () => {
    switch (screen) {
      case screenEnum.PROJECT_DETAILS:
        return <ProjectDescriptionScreen />;

      case screenEnum.ALL_PARTS:
        return <AllPartsScreen />;

      case screenEnum.MANUFACTURERS:
        return <ShortlistedManufacturerScreen setScreen={setScreen} />;

      case screenEnum.DOCUMENTS:
        return <DocumentsScreen />;

      case screenEnum.MESSAGES:
        return <MessagesScreen />;
      default:
        break;
    }
  };

  // clearing redux state
  useEffect(() => {
    return () => {
      dispatch(clearProjectDetailsAction());
    };
  }, []);

  return (
    <div className="font-roboto bg-backgroundGray min-h-[calc(100vh-70px)] relative">
      <div className="absolute left-6 top-3">
        <Breadcrumb
          steps={steps}
          className="bg text-sm w-full"
          currentStep={sectionId}
        />
      </div>
      <div className="grid grid-cols-12 min-h-[calc(100vh-70px)]">
        <div className="lg:hidden col-span-12 p-4">
          <Drawer>
            <DrawerTrigger asChild>
              <Menu size={28} className="cursor-pointer mt-7 ml-5" />
            </DrawerTrigger>
            <DrawerContent>
              <div className="mx-auto w-full max-w-sm">
                <DrawerHeader>
                  <DrawerTitle>Menu</DrawerTitle>
                </DrawerHeader>
                <LeftSidebar role="buyer" />
                <DrawerClose asChild>
                  <Button variant="outline" className="w-full">
                    Close
                  </Button>
                </DrawerClose>
              </div>
            </DrawerContent>
          </Drawer>
        </div>

        <div className="col-span-12 flex gap-2 bg-white px-2 mt-10 mb-4 mx-10 rounded-t-lg relative overflow-hidden">
          {/* left Sidebar */}
          <div className="min-w-[260px] bg-gray-50 mt-2 rounded-lg px-4 py-2">
            <div className=" flex flex-col gap-2 text-base text-gray-700">
              <button
                onClick={() => setScreen('PROJECT_DETAILS')}
                className={`flex items-center gap-4 p-2 rounded-md ${
                  screen === 'PROJECT_DETAILS' || screen === 'ALL_PARTS'
                    ? 'bg-white text-darkPurple'
                    : 'hover:bg-veryVeryLightPurple'
                } transition-all ease-in duration-200`}
              >
                <LayoutDashboard className="w-5 h-5" />
                Project Dashboard
              </button>

              <div className="relative mx-2 mr-8 mt-3 pl-4 space-y-4 text-[16px] before:absolute before:top-0 before:left-2 before:bottom-4 before:w-px before:bg-gray-300">
                <button
                  onClick={() => setScreen('PROJECT_DETAILS')}
                  className={`relative block w-full text-left ml-4 rounded px-2 py-1 before:absolute before:top-1/2 before:-left-6 before:w-4 before:h-px before:bg-gray-300 ${
                    screen === 'PROJECT_DETAILS'
                      ? 'bg-veryVeryLightPurple text-black'
                      : 'text-gray-600 hover:bg-veryVeryLightPurple'
                  } transition-all ease-in duration-200`}
                >
                  Project Details
                </button>

                {projectId !== undefined && (
                  <button
                    onClick={() => setScreen('ALL_PARTS')}
                    className={`relative block w-full text-left rounded ml-4 mr-2 px-2 py-1 before:absolute before:top-1/2 before:-left-6 before:w-4 before:h-px before:bg-gray-300 ${
                      screen === 'ALL_PARTS'
                        ? 'bg-veryVeryLightPurple text-black'
                        : 'text-gray-600 hover:bg-veryVeryLightPurple'
                    } transition-all ease-in duration-200`}
                  >
                    All Parts
                  </button>
                )}
              </div>

              {/* Manufacturers */}
              <button
                onClick={() => setScreen('MANUFACTURERS')}
                className={`flex items-center gap-4 p-2 mt-2 rounded-md ${
                  screen === 'MANUFACTURERS'
                    ? 'bg-white text-darkPurple'
                    : 'hover:bg-veryVeryLightPurple'
                } transition-all ease-in duration-200`}
              >
                <Users className="w-5 h-5" />
                Manufacturers{' '}
              </button>

              {/* Documents */}
              {projectId !== undefined && (
                <button
                  onClick={() => setScreen('DOCUMENTS')}
                  className={`flex items-center gap-4 p-2 mt-2 rounded-md ${
                    screen === 'DOCUMENTS'
                      ? 'bg-white text-darkPurple'
                      : 'hover:bg-veryVeryLightPurple'
                  } transition-all ease-in duration-200`}
                >
                  <FileText className="w-5 h-5" />
                  Documents
                </button>
              )}

              {/* Messages */}
              {projectId !== undefined && (
                <button
                  onClick={() => setScreen('MESSAGES')}
                  className={`flex items-center gap-4 p-2 mt-2 rounded-md relative ${
                    screen === 'MESSAGES'
                      ? 'bg-white text-darkPurple'
                      : 'hover:bg-veryVeryLightPurple'
                  } transition-all ease-in duration-200`}
                >
                  <MessageSquare className="w-5 h-5" />
                  Messages
                </button>
              )}
            </div>
          </div>
          {/* Main Content */}
          <div className="w-full" key={screen}>
            {screenFunction()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsPage;
